import React, { useState, useEffect } from "react";
import { useHRMS } from "../context/HRMSContext.jsx";
import api from "../services/api.js";
import Message from "../components/Message.jsx";
import PermissionGuard from "../components/PermissionGuard.jsx";
import { MODULES, ACTIONS } from "../context/PermissionsContext.jsx";
import CommonTable from "../components/common/CommonTable.jsx";

const calculateWorkingHours = (start, end) => {
  if (!start || !end) return "";

  const startTime = new Date(`1970-01-01T${start}:00`);
  let endTime = new Date(`1970-01-01T${end}:00`);

  // Cross-midnight handling
  if (endTime <= startTime) {
    endTime.setDate(endTime.getDate() + 1);
  }

  const diffMs = endTime - startTime;
  const hours = diffMs / (1000 * 60 * 60);

  return hours.toFixed(1); // 0.5 precision
};

const parseDaysArray = (days) => {
  return Array.isArray(days) ? days : days ? JSON.parse(days) : [];
};

function ShiftMaster() {
  const { state, loadShifts } = useHRMS();
  const [showModal, setShowModal] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    shiftName: "",
    days: [],
    shiftStart: "",
    shiftEnd: "",
    workingHours: "",
    otStart: "",
    otEnd: "",
    graceTime: "",
    penalty: "",
  });
  const [message, setMessage] = useState({ text: "", type: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [actionLoadingText, setActionLoadingText] = useState("Processing...");
  const [selectedShift, setSelectedShift] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const [shiftFilters, setShiftFilters] = useState({
    shiftName: "",
    workingHours: "",
    graceTime: "",
    shiftTime: "",
  });

  // Load shifts when component mounts
  useEffect(() => {
    loadShifts();
  }, []);

  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  if (!daysOfWeek || daysOfWeek.length === 0) {
    throw new Error("Days of week is required");
  }
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (formData.days.length === 0) {
      setMessage({ text: "Please select at least one day!", type: "error" });
      return;
    }

    // Check for duplicate shift name
    const isDuplicate = state.shifts?.some(
      (shift) =>
        shift.shiftName.toLowerCase() === formData.shiftName.toLowerCase() &&
        shift.id !== editingShift?.id,
    );

    if (isDuplicate) {
      setMessage({ text: "Shift name already exists!", type: "error" });
      return;
    }
    // 🚨 Prevent duplicate shift timing (same start & end)
    const isTimeDuplicate = state.shifts?.some(
      (shift) =>
        shift.shiftStart === formData.shiftStart &&
        shift.shiftEnd === formData.shiftEnd &&
        shift.id !== editingShift?.id
    );

    if (isTimeDuplicate) {
      setMessage({
        text: "A shift with same start and end time already exists!",
        type: "error",
      });
      return;
    }

    // Validate OT fields - if OT Start is filled, OT End is required
    if (formData.otStart && !formData.otEnd) {
      setMessage({
        text: "OT End time is required when OT Start is filled",
        type: "error",
      });
      return;
    }

    // If OT End is filled, OT Start is required
    if (!formData.otStart && formData.otEnd) {
      setMessage({
        text: "OT Start time is required when OT End is filled",
        type: "error",
      });
      return;
    }

    // Validate OT Start must be after Shift End
    if (
      formData.otStart &&
      formData.shiftEnd &&
      formData.otStart <= formData.shiftEnd
    ) {
      setMessage({
        text: "OT Start must be after Shift End time",
        type: "error",
      });
      return;
    }

    // Validate shift duration against working hours
    if (formData.shiftStart && formData.shiftEnd && formData.workingHours) {
      const startTime = new Date(`1970-01-01T${formData.shiftStart}:00`);
      const endTime = new Date(`1970-01-01T${formData.shiftEnd}:00`);

      let shiftDuration;
      if (endTime < startTime) {
        // Cross-midnight shift - add 24 hours to end time
        const adjustedEndTime = new Date(
          endTime.getTime() + 24 * 60 * 60 * 1000,
        );
        shiftDuration = (adjustedEndTime - startTime) / (1000 * 60 * 60);
      } else {
        shiftDuration = (endTime - startTime) / (1000 * 60 * 60);
      }

      if (shiftDuration > formData.workingHours) {
        setMessage({
          text: `Shift duration (${shiftDuration.toFixed(
            1,
          )} hours) cannot exceed working hours (${formData.workingHours
            } hours)`,
          type: "error",
        });
        return;
      }
    }

    try {
      setIsSubmitting(true);
      const shiftData = {
        shiftName: formData.shiftName,
        days: JSON.stringify(formData.days),
        shiftStart: formData.shiftStart,
        shiftEnd: formData.shiftEnd,
        workingHours: parseFloat(formData.workingHours),
        otStart: formData.otStart || null,
        otEnd: formData.otEnd || null,
        graceTime: parseFloat(formData.graceTime) || 0,
        penalty: parseFloat(formData.penalty) || 0,
      };

      console.log("Sending shift data:", shiftData);

      if (editingShift) {
        await api.shifts.update(editingShift.id, shiftData);
        setMessage({ text: "Shift updated successfully!", type: "success" });
      } else {
        await api.shifts.create(shiftData);
        setMessage({ text: "Shift created successfully!", type: "success" });
      }
      await loadShifts();
      setTableRefreshKey((k) => k + 1);
      resetForm();
    } catch (error) {
      console.error("Error saving shift:", error);
      const errorMsg = error.response?.data?.error || "Error saving shift!";
      setMessage({ text: errorMsg, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    if (isSubmitting) return;
    setFormData({
      shiftName: "",
      days: [],
      shiftStart: "",
      shiftEnd: "",
      workingHours: "",
      otStart: "",
      otEnd: "",
      graceTime: "",
      penalty: "",
    });
    setShowModal(false);
    setEditingShift(null);
  };

  const handleEdit = async (shift) => {
    if (isActionLoading) return;
    setIsActionLoading(true);
    setActionLoadingText("Loading shift details...");
    try {
      await new Promise((resolve) => setTimeout(resolve, 200));
      setEditingShift(shift);
      const editData = {
        ...shift,
        days: parseDaysArray(shift.days),
      };
      setFormData(editData);
      setShowModal(true);
    } catch (error) {
      console.error("Error opening edit shift:", error);
    } finally {
      setIsActionLoading(false);
    }
  };
  const filteredShifts = (state.shifts || []).filter((shift) => {

    const matchesShiftName =
      !shiftFilters.shiftName ||
      shift.shiftName
        .toLowerCase()
        .includes(shiftFilters.shiftName.toLowerCase());

    const matchesWorkingHours =
      !shiftFilters.workingHours ||
      Number(shift.workingHours || 0) ===
      Number(shiftFilters.workingHours);

    const matchesGraceTime =
      !shiftFilters.graceTime ||
      Number(shift.graceTime || 0) ===
      Number(shiftFilters.graceTime);

    const shiftTime =
      `${shift.shiftStart} - ${shift.shiftEnd}`.toLowerCase();

    const matchesShiftTime =
      !shiftFilters.shiftTime ||
      shiftTime.includes(
        shiftFilters.shiftTime.toLowerCase()
      );

    return (
      matchesShiftName &&
      matchesWorkingHours &&
      matchesGraceTime &&
      matchesShiftTime
    );
  });

  const handleInfo = (shift) => {
    setSelectedShift(shift);
    setShowInfoModal(true);
  };

  const handleToggleStatus = async (shift) => {
    if (isActionLoading) return;
    const currentStatus = shift.status ?? 1;
    const newStatus = currentStatus === 1 ? 2 : 1;
    const isActivating = newStatus === 1;
    const actionText = isActivating ? "activate" : "inactivate";
    const shiftName = shift.shiftName || "this shift";

    if (window.confirm(`Are you sure you want to ${actionText} ${shiftName}?`)) {
      setIsActionLoading(true);
      setActionLoadingText(`${isActivating ? "Activating" : "Inactivating"} shift...`);
      try {
        await api.shifts.toggleStatus(shift.id, newStatus);
        await loadShifts();
        setTableRefreshKey((k) => k + 1);
        setMessage({
          text: `Shift ${isActivating ? "activated" : "inactivated"} successfully!`,
          type: "success",
        });
      } catch (error) {
        console.error("Error toggling shift status:", error);
        setMessage({
          text: error.message || `Failed to ${actionText} shift`,
          type: "error",
        });
      } finally {
        setIsActionLoading(false);
      }
    }
  };

  const handleDelete = async (id) => {
    if (isActionLoading) return;
    if (window.confirm("Are you sure?")) {
      setIsActionLoading(true);
      setActionLoadingText("Deleting shift...");
      try {
        await api.shifts.delete(id);
        await loadShifts();
        setTableRefreshKey((k) => k + 1);
        setMessage({ text: "Shift deleted successfully!", type: "success" });
      } catch (error) {
        console.error("Error deleting shift:", error);
        setMessage({ text: "Error deleting shift!", type: "error" });
      } finally {
        setIsActionLoading(false);
      }
    }
  };

  const handleDayChange = (day) => {
    const updatedDays = formData.days.includes(day)
      ? formData.days.filter((d) => d !== day)
      : [...formData.days, day];
    setFormData({ ...formData, days: updatedDays });
  };

  return (
    <PermissionGuard
      module={MODULES.SHIFTS}
      action={ACTIONS.VIEW}
      submodule={MODULES.SHIFTS.submodules.SHIFT_MASTER}
    >
      <div className="shift-master">
        {/* Full-screen blocking loader when action is triggered */}
        {isActionLoading && (
          <div
            className="full-screen-action-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              inset: 0,
              width: "100%",
              height: "100%",
              minWidth: "100%",
              minHeight: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
              zIndex: 999999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pointerEvents: "all",
              cursor: "wait",
            }}
          >
            <div
              className="full-screen-action-card"
              style={{
                background: "#ffffff",
                padding: "18px 28px",
                borderRadius: "8px",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
                display: "flex",
                alignItems: "center",
                gap: "14px",
                pointerEvents: "auto",
              }}
            >
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: "2rem", height: "2rem" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <span
                className="full-screen-action-text"
                style={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#212529",
                  letterSpacing: "0.2px",
                }}
              >
                {actionLoadingText || "Processing..."}
              </span>
            </div>
          </div>
        )}

        {message.text && (
          <Message
            message={message.text}
            type={message.type}
            onClose={() => setMessage({ text: "", type: "" })}
          />
        )}
        <div className="d-flex justify-content-between align-items-center mb-1">

          <h2 className="hd_a">Shift Master</h2>

          <div className="d-flex align-items-center gap-2">

            <button
              type="button"
              className={`btn ${Object.values(shiftFilters).some(v => v !== "")
                  ? "btn-primary"
                  : "btn-outline-primary"
                }`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <i className="bi bi-funnel"></i>
            </button>

            <PermissionGuard
              module={MODULES.SHIFTS}
              action={ACTIONS.CREATE}
              submodule={MODULES.SHIFTS.submodules.SHIFT_MASTER}
            >
              <button
                className="btn btn-primary"
                onClick={() => setShowModal(true)}
              >
                <i className="bi bi-plus-lg me-1"></i> Add Shift
              </button>
            </PermissionGuard>

          </div>
        </div>
        {/* <h2 className="hd_a">Shift Master</h2>
          <PermissionGuard
            module={MODULES.SHIFTS}
            action={ACTIONS.CREATE}
            submodule={MODULES.SHIFTS.submodules.SHIFT_MASTER}
          >
            <button
              className="btn btn-primary"
              onClick={() => setShowModal(true)}
            >
              <i className="bi bi-plus-lg me-1"></i> Add Shift
            </button>
          </PermissionGuard>
        </div>
 */}

        {showFilters && (
          <div className="card shadow-sm mb-3">
            <div className="card-body py-2">

              <div className="row g-3">

                <div className="col-md-3">
                  <label className="form-label">Shift Name</label>

                  <select
                    className="form-control"
                    value={shiftFilters.shiftName}
                    onChange={(e) =>
                      setShiftFilters({
                        ...shiftFilters,
                        shiftName: e.target.value,
                      })
                    }
                  >
                    <option value="">All Shifts</option>

                    {(state.shifts || []).map((shift) => (
                      <option key={shift.id} value={shift.shiftName}>
                        {shift.shiftName}
                      </option>
                    ))}
                  </select></div>

                <div className="col-md-2">
                  <label className="form-label">Working Hours</label>

                  <input
                    type="number"
                    className="form-control"
                    placeholder="Hours"
                    value={shiftFilters.workingHours}
                    onChange={(e) =>
                      setShiftFilters({
                        ...shiftFilters,
                        workingHours: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-md-2">
                  <label className="form-label">Grace Time</label>

                  <input
                    type="number"
                    className="form-control"
                    placeholder="Grace"
                    value={shiftFilters.graceTime}
                    onChange={(e) =>
                      setShiftFilters({
                        ...shiftFilters,
                        graceTime: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Shift Time</label>

                  <input
                    type="text"
                    className="form-control"
                    placeholder="09:00 - 18:00"
                    value={shiftFilters.shiftTime}
                    onChange={(e) =>
                      setShiftFilters({
                        ...shiftFilters,
                        shiftTime: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-md-2 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-outline-primary w-100"
                    onClick={() =>
                      setShiftFilters({
                        shiftName: "",
                        workingHours: "",
                        graceTime: "",
                        shiftTime: "",
                      })
                    }
                  >
                    Clear
                  </button>
                </div>

              </div>

            </div>
          </div>
        )}

        {showModal && (
          <div
            className="modal"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.3)", pointerEvents: isSubmitting ? "none" : "auto" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-sm border-0">
                <div
                  className="modal-header border-bottom"
                  style={{ backgroundColor: "var(--primary-light)" }}
                >
                  <h6
                    className="modal-title"
                    style={{ color: "var(--primary-color)" }}
                  >
                    {editingShift ? "Edit" : "Add"} Shift
                  </h6>
                  <button
                    type="button"
                    className="btn-close"
                    disabled={isSubmitting}
                    onClick={resetForm}
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body p-3">
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="form-label small text-muted">
                          Shift Name *
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={formData.shiftName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              shiftName: e.target.value,
                            })
                          }
                          required
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted">
                          Grace Time (Hours) *
                        </label>
                        <input
                          type="number"
                          step="0.01" // 👈 allows decimals
                          min="0"
                          className="form-control form-control-sm"
                          value={formData.graceTime}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              graceTime: parseFloat(e.target.value) || 0,
                            })
                          }
                          required
                        />
                      </div>

                      <div className="col-12">
                        <label className="form-label small text-muted">
                          Days *
                        </label>
                        <div className="d-flex flex-wrap gap-2">
                          {daysOfWeek.map((day) => (
                            <div key={day} className="form-check form-check-sm">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={formData.days.includes(day)}
                                onChange={() => handleDayChange(day)}
                              />
                              <label className="form-check-label small text-muted">
                                {day.slice(0, 3)}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted">
                          Shift Start *
                        </label>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          value={formData.shiftStart}
                          onChange={(e) => {
                            const shiftStart = e.target.value;
                            const workingHours = calculateWorkingHours(
                              shiftStart,
                              formData.shiftEnd,
                            );

                            setFormData({
                              ...formData,
                              shiftStart,
                              workingHours,
                            });
                          }}
                          required
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted">
                          Shift End *
                        </label>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          value={formData.shiftEnd}
                          onChange={(e) => {
                            const shiftEnd = e.target.value;
                            const workingHours = calculateWorkingHours(
                              formData.shiftStart,
                              shiftEnd,
                            );

                            setFormData({
                              ...formData,
                              shiftEnd,
                              workingHours,
                            });
                          }}
                          required
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted">
                          Working Hours *
                        </label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={formData.workingHours}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              workingHours: e.target.value,
                            })
                          }
                          min="1"
                          max="24"
                          step="0.5"
                          placeholder="Enter working hours"
                          readOnly
                        />
                        <small className="text-muted">
                          Maximum hours per shift (shift duration cannot exceed
                          this)
                        </small>

                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted">
                          Penalty %
                        </label>
                        <input
                          type="number"
                          step="0.01" // 👈 allows decimals
                          min="0"
                          className="form-control form-control-sm"
                          value={formData.penalty || ""}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              penalty: parseFloat(e.target.value) || 0,
                            })
                          }

                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted">
                          OT Start
                        </label>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          value={formData.otStart}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              otStart: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted">
                          OT End {formData.otStart ? "*" : ""}
                        </label>
                        <input
                          type="time"
                          className="form-control form-control-sm"
                          value={formData.otEnd}
                          onChange={(e) =>
                            setFormData({ ...formData, otEnd: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer bg-light py-2">
                    <button type="submit" className="btn btn-sm btn-primary" disabled={isSubmitting}>
                      {isSubmitting && (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      )}
                      {isSubmitting ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={isSubmitting}
                      onClick={resetForm}
                    >
                      Close
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Shift Info Modal */}
        {showInfoModal && selectedShift && (
          <div
            className="modal show d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow">
                <div
                  className="modal-header py-2"
                  style={{
                    backgroundColor: "var(--primary-color, #0d6efd)",
                    color: "#fff",
                  }}
                >
                  <h6 className="modal-title mb-0">Shift Details</h6>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => {
                      setShowInfoModal(false);
                      setSelectedShift(null);
                    }}
                  ></button>
                </div>
                <div className="modal-body p-3">
                  <table className="table table-bordered table-sm mb-0">
                    <tbody>
                      <tr>
                        <th style={{ width: "35%", background: "#f8f9fa" }}>
                          Shift Name
                        </th>
                        <td>{selectedShift.shiftName}</td>
                      </tr>
                      <tr>
                        <th style={{ background: "#f8f9fa" }}>Status</th>
                        <td>
                          {(selectedShift.status ?? 1) === 1 ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-danger">Inactive</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ background: "#f8f9fa" }}>Days</th>
                        <td>
                          {parseDaysArray(selectedShift.days).join(", ") ||
                            "None"}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ background: "#f8f9fa" }}>Shift Timing</th>
                        <td>{`${selectedShift.shiftStart || "--:--"} - ${selectedShift.shiftEnd || "--:--"
                          }`}</td>
                      </tr>
                      <tr>
                        <th style={{ background: "#f8f9fa" }}>Working Hours</th>
                        <td>{`${selectedShift.workingHours || 8} hrs`}</td>
                      </tr>
                      <tr>
                        <th style={{ background: "#f8f9fa" }}>OT Timing</th>
                        <td>
                          {selectedShift.otStart && selectedShift.otEnd
                            ? `${selectedShift.otStart} - ${selectedShift.otEnd}`
                            : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ background: "#f8f9fa" }}>Grace Time</th>
                        <td>{`${selectedShift.graceTime || 0} hrs`}</td>
                      </tr>
                      <tr>
                        <th style={{ background: "#f8f9fa" }}>Penalty</th>
                        <td>{`${selectedShift.penalty || 0}%`}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="modal-footer p-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setShowInfoModal(false);
                      setSelectedShift(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-body px-0 pt-0">
            <CommonTable
              key={tableRefreshKey}
              dataSource={api.shifts.table}
              queryParams={{
                shiftName: shiftFilters.shiftName,
                workingHours: shiftFilters.workingHours,
                graceTime: shiftFilters.graceTime,
                shiftTime: shiftFilters.shiftTime,
              }}
              columns={[
                { key: "shiftName", label: "Shift Name", sortable: true },
                {
                  key: "days",
                  label: "Days",
                  sortable: false,
                  render: (value, shift) =>
                    parseDaysArray(shift.days).join(", "),
                },
                {
                  key: "shiftTime",
                  label: "Shift Time",
                  sortable: false,
                  render: (value, shift) =>
                    `${shift.shiftStart} - ${shift.shiftEnd}`,
                },
                {
                  key: "workingHours",
                  label: "Working Hours",
                  sortable: true,
                  render: (shift) => `${shift.workingHours || 8} hrs`,
                },
                {
                  key: "otTime",
                  label: "OT Time",
                  sortable: false,
                  render: (value, shift) =>
                    `${shift.otStart} - ${shift.otEnd}`,
                },
                {
                  key: "graceTime",
                  label: "Grace Time",
                  sortable: true,
                  render: (shift) => `${shift.graceTime || 0} hrs`,
                },
                {
                  key: "status",
                  label: "Status",
                  sortable: true,
                  render: (value, shift) =>
                    (shift.status ?? 1) === 1 ? (
                      <span
                        className="badge bg-success"
                        style={{ fontSize: "11px", padding: "4px 8px" }}
                      >
                        Active
                      </span>
                    ) : (
                      <span
                        className="badge bg-danger"
                        style={{ fontSize: "11px", padding: "4px 8px" }}
                      >
                        Inactive
                      </span>
                    ),
                },
              ]}
              renderActions={(shift) => {
                const isActive = (shift.status ?? 1) === 1;
                return (
                  <div className="d-flex align-items-center gap-1">
                    <button
                      title="View Details"
                      className="btn btn-sm btn-primary"
                      disabled={isActionLoading}
                      onClick={() => handleInfo(shift)}
                      style={{ fontSize: "13px", padding: "4px 10px" }}
                    >
                      <i className="bi bi-info-circle-fill"></i>
                    </button>
                    {isActive && (
                      <PermissionGuard
                        module={MODULES.SHIFTS}
                        action={ACTIONS.EDIT}
                        submodule={MODULES.SHIFTS.submodules.SHIFT_MASTER}
                      >
                        <button
                          title="Edit"
                          className="btn btn-sm btn-primary"
                          disabled={isActionLoading}
                          onClick={() => handleEdit(shift)}
                          style={{ fontSize: "13px", padding: "4px 10px" }}
                        >
                          <i className="bi bi-pencil-square"></i>
                        </button>
                      </PermissionGuard>
                    )}
                    {/* Hard delete commented out as per soft delete requirement
                    <PermissionGuard
                      module={MODULES.SHIFTS}
                      action={ACTIONS.DELETE}
                      submodule={MODULES.SHIFTS.submodules.SHIFT_MASTER}
                    >
                      <button
                        title="Delete"
                        className="btn btn-sm btn-danger"
                        disabled={isActionLoading}
                        onClick={() => handleDelete(shift.id)}
                        style={{ fontSize: "13px", padding: "4px 10px" }}
                      >
                        <i className="bi bi-trash-fill"></i>
                      </button>
                    </PermissionGuard>
                    */}
                    <button
                      title={isActive ? "Inactivate Shift" : "Activate Shift"}
                      className={`btn btn-sm ${isActive ? "btn-outline-danger" : "btn-outline-success"
                        }`}
                      onClick={() => handleToggleStatus(shift)}
                      disabled={isActionLoading}
                      style={{ fontSize: "13px", padding: "4px 10px" }}
                    >
                      {isActive ? (
                        <i className="bi bi-slash-circle" title="Inactivate"></i>
                      ) : (
                        <i className="bi bi-check-circle" title="Activate"></i>
                      )}
                    </button>
                  </div>
                );
              }}
            />
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
}

export default ShiftMaster;
